import api from './api';
import { t } from '../i18n';

// ============================================
// Globale Suche — seit 2026-09-01 über den ZENTRALEN Backend-Endpoint
// GET /api/search (durchsucht alle öffentlichen Inhalte serverseitig).
// Dazu: GET /api/search/discover für den gefüllten Empty-State des
// Such-Explorers + lokale Suchhistorie (localStorage).
//
// WICHTIG: Alles defensiv parsen – Felder können fehlen oder null sein!
// ============================================

export type SearchResultType =
  | 'tournament'
  | 'club'
  | 'pub'
  | 'shop'
  | 'series'
  | 'league'
  | 'association'
  | 'news'
  | 'forum'
  | 'proPlayer'
  | 'player'
  | 'prediction'
  | 'knowledge'
  | 'help'
  | 'proTournament';

export interface SearchResult {
  type: SearchResultType;
  id: string;
  title: string;
  subtitle?: string | null;
  imageUrl?: string | null;
  /** Ziel-Link zur Detail-Seite (null, wenn kein Slug vorhanden) */
  link?: string | null;
}

export type GroupedSearchResults = Record<SearchResultType, SearchResult[]>;

export const SEARCH_TYPE_LABELS: Record<SearchResultType, string> = {
  proPlayer: 'Profis',
  player: 'Spieler',
  tournament: 'Turniere',
  club: 'Vereine',
  pub: t('web.component.footer.spielstaetten'),
  shop: 'Shops',
  series: 'Turnierserien',
  league: 'Ligen',
  association: t('web.component.footer.verbaende'),
  news: 'News',
  forum: 'Forum',
  prediction: 'Tippspiele',
  knowledge: 'Darts-Wissen',
  help: 'Hilfe',
  proTournament: 'PDC-Turniere',
};

// PDC-Turniere ganz oben — danach wird am meisten gesucht (Marcel 2026-09-01)
export const SEARCH_TYPE_ORDER: SearchResultType[] = [
  'proTournament',
  'proPlayer',
  'player',
  'tournament',
  'club',
  'pub',
  'shop',
  'series',
  'league',
  'association',
  'news',
  'forum',
  'prediction',
  'knowledge',
  'help',
];

// Backend-Gruppen (results.<key>) → Frontend-Typ + Link-Muster
const BACKEND_GROUPS: Record<string, { type: SearchResultType; link: (slug: string) => string }> = {
  proPlayers: { type: 'proPlayer', link: (s) => `/spieler/${s}` },
  players: { type: 'player', link: (s) => `/community/profil/${encodeURIComponent(s)}` },
  news: { type: 'news', link: (s) => `/news/${s}` },
  tournaments: { type: 'tournament', link: (s) => `/turnier/${s}` },
  series: { type: 'series', link: (s) => `/turnierserie/${s}` },
  clubs: { type: 'club', link: (s) => `/verein/${s}` },
  pubs: { type: 'pub', link: (s) => `/spielstaette/${s}` },
  shops: { type: 'shop', link: (s) => `/shop/${s}` },
  leagues: { type: 'league', link: (s) => `/liga/${s}` },
  associations: { type: 'association', link: (s) => `/verband/${s}` },
  forumTopics: { type: 'forum', link: (s) => `/forum/topic/${s}` },
  predictions: { type: 'prediction', link: (s) => `/tippspiele/${s}` },
  knowledge: { type: 'knowledge', link: (s) => `/info/${s}` },
  help: { type: 'help', link: (s) => `/hilfe/${s}` },
  proTournaments: { type: 'proTournament', link: (s) => `/turniere/${s}` },
};

const safeString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

/** Bild-Pfade des Backends in absolute URLs auflösen (fail-soft). */
export const resolveSearchImage = (image?: string | null): string | null => {
  const value = safeString(image);
  if (!value) return null;
  if (value.startsWith('http')) return value;
  const base = (api.defaults.baseURL || '').replace(/\/+$/, '');
  if (value.startsWith('/')) return `${base}${value}`;
  return `${base}/api/file/${value}`;
};

/** Datum formatieren (Heute / Morgen / dd.MM.yyyy). */
const formatDate = (timestamp?: number | null): string | null => {
  if (!timestamp || typeof timestamp !== 'number' || isNaN(timestamp)) return null;
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  if (dateOnly === today) return t('web.component.home-live-pdc-lead.heute');
  if (dateOnly === today + 24 * 60 * 60 * 1000) return t('web.component.day-selector.morgen');
  return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
    .toString()
    .padStart(2, '0')}.${date.getFullYear()}`;
};

/** Ein Backend-Item ({id,type,title,subtitle,image,slug,date}) → SearchResult */
export const mapBackendItem = (
  raw: any,
  type: SearchResultType,
  linkBuilder: (slug: string) => string,
): SearchResult | null => {
  const id = safeString(raw?.id);
  // Anzeigename (Spieler-Profile) hat Vorrang vor dem Titel/Username —
  // defensiv: fehlt das Feld (altes Backend/andere Typen), greift title.
  const title = safeString(raw?.displayName) ?? safeString(raw?.title);
  if (!id || !title) return null;
  const slug = safeString(raw?.slug);
  const dateLabel = formatDate(typeof raw?.date === 'number' ? raw.date : null);
  const subtitle =
    [safeString(raw?.subtitle), dateLabel].filter(Boolean).join(' • ') || null;
  return {
    type,
    id,
    title,
    subtitle,
    imageUrl: resolveSearchImage(raw?.image),
    link: slug ? linkBuilder(slug) : null,
  };
};

const emptyResults = (): GroupedSearchResults =>
  SEARCH_TYPE_ORDER.reduce((acc, type) => {
    acc[type] = [];
    return acc;
  }, {} as GroupedSearchResults);

// ============================================
// Suchhistorie (localStorage, max. 8 Einträge)
// ============================================

const RECENT_KEY = 'dc-recent-searches';
const RECENT_MAX = 8;

// ============================================
// Discover-Inhalte (Empty-State-Slider)
// ============================================

export interface DiscoverSections {
  proPlayers: SearchResult[];
  news: SearchResult[];
  tournaments: SearchResult[];
  players: SearchResult[];
}

class SearchService {
  /** Mindestlänge des Suchbegriffs (wie Backend-Validation). */
  readonly minQueryLength = 2;

  /**
   * Globale Suche über den zentralen Backend-Endpoint.
   * Fail-soft: bei Fehlern leere Gruppen statt Exception.
   */
  async searchAll(query: string): Promise<GroupedSearchResults> {
    const trimmed = (query || '').trim();
    const results = emptyResults();
    if (trimmed.length < this.minQueryLength) return results;

    try {
      const response = await api.get('/api/search', { params: { q: trimmed, limit: 6 } });
      const groups = response?.data?.results;
      if (!groups || typeof groups !== 'object') return results;
      for (const [key, config] of Object.entries(BACKEND_GROUPS)) {
        const items = Array.isArray(groups[key]) ? groups[key] : [];
        results[config.type] = items
          .map((raw: any) => mapBackendItem(raw, config.type, config.link))
          .filter((r: SearchResult | null): r is SearchResult => r !== null);
      }
      return results;
    } catch (error) {
      console.error('Zentrale Suche fehlgeschlagen:', error);
      return results;
    }
  }

  /** Discover-Inhalte für den leeren Such-Explorer (fail-soft → null). */
  async getDiscover(): Promise<DiscoverSections | null> {
    try {
      const response = await api.get('/api/search/discover');
      const sections = response?.data?.sections;
      if (!sections || typeof sections !== 'object') return null;
      const mapGroup = (key: string): SearchResult[] => {
        const config = BACKEND_GROUPS[key];
        const items = Array.isArray(sections[key]) ? sections[key] : [];
        return items
          .map((raw: any) => mapBackendItem(raw, config.type, config.link))
          .filter((r: SearchResult | null): r is SearchResult => r !== null);
      };
      return {
        proPlayers: mapGroup('proPlayers'),
        news: mapGroup('news'),
        tournaments: mapGroup('tournaments'),
        players: mapGroup('players'),
      };
    } catch (error) {
      console.error('Discover-Inhalte fehlgeschlagen:', error);
      return null;
    }
  }

  /** Gesamtanzahl aller Treffer. */
  countResults(results?: GroupedSearchResults | null): number {
    if (!results) return 0;
    return SEARCH_TYPE_ORDER.reduce(
      (sum, type) => sum + (Array.isArray(results[type]) ? results[type].length : 0),
      0,
    );
  }

  /** Leeres Ergebnis-Objekt (z. B. für Initial-State). */
  emptyResults(): GroupedSearchResults {
    return emptyResults();
  }

  /** Zuletzt gesuchte Begriffe (neueste zuerst). */
  getRecentSearches(): string[] {
    try {
      const raw = window.localStorage.getItem(RECENT_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((s) => typeof s === 'string') : [];
    } catch {
      return [];
    }
  }

  /** Begriff in die Historie aufnehmen (dedupliziert, max. 8). */
  addRecentSearch(query: string): void {
    const trimmed = (query || '').trim();
    if (trimmed.length < this.minQueryLength) return;
    try {
      const current = this.getRecentSearches().filter(
        (s) => s.toLowerCase() !== trimmed.toLowerCase(),
      );
      window.localStorage.setItem(
        RECENT_KEY,
        JSON.stringify([trimmed, ...current].slice(0, RECENT_MAX)),
      );
    } catch {
      /* localStorage nicht verfügbar → Historie einfach weglassen */
    }
  }

  clearRecentSearches(): void {
    try {
      window.localStorage.removeItem(RECENT_KEY);
    } catch {
      /* ignorieren */
    }
  }
}

export default new SearchService();
